// Angular imports
import { CommonModule } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, NgModule } from '@angular/core';

// devextreme imports
import { DxBoxModule, DxButtonModule, DxPopupModule } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// Service imports
import { DatadogService } from '../_services/datadog.service';
import { EnvironmentConfigService } from '../_services/environment-config.service';

// Model imports
import { LoginParameterDto } from '../_models/login-parameter-dto';

@Component({
  selector: 'app-dev-tools',
  templateUrl: './dev-tools.component.html',
  styleUrls: ['./dev-tools.component.scss'],
  standalone: false
})
export class DevToolsComponent {
  devToolsVisible = false;

  constructor(
    private readonly http: HttpClient,
    private readonly envConfig: EnvironmentConfigService,
    private readonly datadogService: DatadogService
  ) {}

  // Early-return in prod. Dev-tools UI is hidden in prod, but handlers
  // remain callable from the JS console; guard prevents synthetic RUM
  // noise / prod WAF 404s from console invocation.
  private get disabledInProd(): boolean {
    return this.envConfig.currentUiEnvironment === 'prod';
  }

  // Throws an uncaught JS error -> exercises RPErrorsHandler (non-HTTP branch)
  // -> datadogService.addError -> RUM records as client error.
  onTestExceptionClick(): void {
    if (this.disabledInProd) return;
    throw new Error(
      'Test exception for Datadog sourcemap verification - this should appear with source code links in Datadog RUM'
    );
  }

  // Hits a nonexistent backend path. The HttpErrorResponse bubbles up
  // unhandled. DatadogErrorInterceptor fires addError at the interceptor
  // layer; RPErrorsHandler displays the user-facing message (but no longer
  // double-reports to Datadog).
  onTestHttpUnhandledClick(): void {
    if (this.disabledInProd) return;
    const url = `${this.envConfig.ccResidentPortalApiUrl}dev-tools-test/unhandled-404`;
    // No .subscribe error handler -> error is unhandled -> RPErrorsHandler fires.
    this.http.get(url).subscribe();
    notify(
      'Firing unhandled GET to a 404 endpoint. Check Datadog RUM.',
      'info',
      3000
    );
  }

  // Hits a nonexistent backend path, catches the error locally (simulating
  // a component that handles its own errors with a toast / logged warning).
  // Without the DatadogErrorInterceptor, this error would NEVER reach RUM
  // because RPErrorsHandler only sees unhandled errors. With the interceptor,
  // RUM still captures full error context. This is the primary scenario the
  // new interceptor is meant to fix.
  onTestHttpCaughtClick(): void {
    if (this.disabledInProd) return;
    const url = `${this.envConfig.ccResidentPortalApiUrl}dev-tools-test/caught-404`;
    this.http.get(url).subscribe({
      error: (err: HttpErrorResponse) => {
        // Swallow the error on purpose — confirms the interceptor still
        // records it to Datadog even when the caller handles the error.
        notify(
          `Caught ${err.status} locally. Interceptor should have reported to Datadog.`,
          'info',
          3000
        );
      }
    });
  }

  // Hits a host that doesn't exist. Surfaces as status=0 (network error).
  // Status-0 errors are dropped by shouldDropHttpError (Filter 1) and should
  // NOT appear in Datadog. Verifies the noise filter is active.
  onTestNetworkErrorClick(): void {
    if (this.disabledInProd) return;
    const url = 'https://definitely-not-a-real-ciranet-host.invalid/api/test';
    this.http.get(url).subscribe({
      error: () => {
        notify(
          'Network error swallowed. Status-0 filter should drop it. Verify NO event in Datadog.',
          'info',
          3000
        );
      }
    });
  }

  // Synthesizes a status-401 HttpErrorResponse and feeds it directly to
  // addHttpError, exercising Filter 2 (drop status 401) in isolation.
  //
  // A real network test isn't reliable here: CredentialsInterceptor forces
  // withCredentials:true on every request, so cookies authenticate the
  // request even when the Authorization header is invalid — auth-required
  // endpoints don't actually return 401 from dev-tools in this app.
  // DatadogErrorInterceptor's only job for an HTTP error is to call
  // addHttpError(err, req.method), so calling it directly here exercises
  // the same filter code path that production 401 events flow through.
  onTestUnauthorizedClick(): void {
    if (this.disabledInProd) return;
    const syntheticError = new HttpErrorResponse({
      status: 401,
      statusText: 'Unauthorized',
      url: `${this.envConfig.ccResidentPortalApiUrl}user/dashboard`,
      error: { Message: 'Synthetic 401 for Filter 2 verification' }
    });
    this.datadogService.addHttpError(syntheticError, 'GET');
    notify(
      'Synthetic 401 sent through addHttpError. Filter 2 should drop it. Verify NO event in Datadog.',
      'info',
      3000
    );
  }

  // POSTs a login with deliberately-invalid credentials, shaped as a real
  // LoginParameterDto so the backend deserializer doesn't reject the payload
  // before reaching the credential-check path. identity-api returns 404 for
  // failed logins by non-standard convention (RFC 7235 calls for 401; tracked
  // in DX-1071). shouldDropHttpError (Filter 4) drops the resident-specific
  // case until the backend fix lands. Verify NO event in Datadog.
  onTestLoginFailureClick(): void {
    if (this.disabledInProd) return;
    const url = `${this.envConfig.ccAuthenticationApiUrl}login`;
    const payload = new LoginParameterDto();
    payload.username = 'dev-tools-test-invalid-user';
    payload.password = 'dev-tools-test-invalid-password';
    payload.recaptchaToken = '';
    this.http.post(url, payload).subscribe({
      error: () => {
        notify(
          '404 expected (wrong credentials). Filter 4 should drop the event. Verify NO event in Datadog.',
          'info',
          3000
        );
      }
    });
  }

  onCloseClick(): void {
    this.devToolsVisible = false;
  }
}

@NgModule({
  imports: [CommonModule, DxPopupModule, DxBoxModule, DxButtonModule],
  declarations: [DevToolsComponent],
  exports: [DevToolsComponent]
})
export class DevToolsModule {}
